/**
 * Humanize Prices — psychological price shaping, capped at ±2% drift.
 * Pure functions, no network.
 */

export type ProductTypeHint = 'single' | 'slab' | 'sealed' | 'lot' | 'accessory' | null | undefined;

export interface HumanizeInput {
	price: number; // dollars
	product_type?: ProductTypeHint;
	title?: string | null;
}

export interface HumanizeResult {
	suggested: number; // dollars, 2dp
	reason: string;
	changed: boolean;
	deltaPct: number;
}

const MAX_DRIFT = 0.02; // ±2%

const UNLUCKY_ENDINGS = ['.13', '.66'];
const UNLUCKY_WHOLES = new Set([13, 66, 666]);

function round2(n: number) {
	return Math.round(n * 100) / 100;
}

function withinDrift(orig: number, candidate: number) {
	if (orig <= 0) return false;
	return Math.abs(candidate - orig) / orig <= MAX_DRIFT + 1e-9;
}

function endsWith(price: number, cents: number) {
	const c = Math.round(price * 100) % 100;
	return c === cents;
}

function isUnlucky(price: number): boolean {
	const str = price.toFixed(2);
	if (UNLUCKY_ENDINGS.some((e) => str.endsWith(e))) return true;
	const whole = Math.floor(price);
	if (UNLUCKY_WHOLES.has(whole)) return true;
	// repeating triples like 111, 222, 333 in the whole part
	const w = String(whole);
	if (w.length >= 3 && /^(\d)\1{2,}$/.test(w)) return true;
	return false;
}

/**
 * Generate candidate prices near `orig` that match preferred endings for a band.
 * Returns candidates sorted by absolute distance from orig.
 */
function candidatesForBand(
	orig: number,
	endings: number[], // cents, 0..99
	stepDollars = 1
): number[] {
	const wholeLo = Math.floor(orig) - Math.ceil(stepDollars);
	const wholeHi = Math.ceil(orig) + Math.ceil(stepDollars);
	const out: number[] = [];
	for (let w = wholeLo; w <= wholeHi; w++) {
		if (w < 0) continue;
		for (const c of endings) {
			out.push(round2(w + c / 100));
		}
	}
	return out
		.filter((p) => p > 0)
		.sort((a, b) => Math.abs(a - orig) - Math.abs(b - orig));
}

function pickBand(price: number, type: ProductTypeHint): number[] {
	// Endings preference per band + product type
	const isSlab = type === 'slab';
	const isSealed = type === 'sealed';

	if (price < 5) return [99, 49];
	if (price < 50) return isSlab || isSealed ? [95, 99, 0] : [99, 95];
	if (price < 200) return isSlab ? [0, 50, 95] : [95, 99, 50];
	if (price < 1000) {
		// nearest $5, endings .00 / .95
		const base = Math.round(price / 5) * 5;
		return [base + 0 - Math.floor(base), (base - 0.05 - Math.floor(base - 0.05)) * 100].map(
			() => 0 // placeholder, handled by wholeStep path below
		);
	}
	return [0]; // handled below
}

function candidatesLargeBand(orig: number, stepDollars: number, wholeEndings: number[]): number[] {
	// wholeEndings = e.g. [0,5] means whole dollars ending in 0 or 5
	const lo = Math.floor(orig) - stepDollars * 2;
	const hi = Math.ceil(orig) + stepDollars * 2;
	const out: number[] = [];
	for (let w = lo; w <= hi; w++) {
		if (w < 0) continue;
		const lastDigit = w % 10;
		if (!wholeEndings.includes(lastDigit)) continue;
		// two endings: .00 and .95 (drops by nickel for charm)
		out.push(round2(w));
		out.push(round2(w - 0.05));
	}
	return out.filter((p) => p > 0).sort((a, b) => Math.abs(a - orig) - Math.abs(b - orig));
}

export function humanizePrice(input: HumanizeInput): HumanizeResult {
	const orig = round2(input.price);
	if (!Number.isFinite(orig) || orig <= 0) {
		return { suggested: orig, reason: 'invalid price', changed: false, deltaPct: 0 };
	}

	const type = input.product_type ?? null;

	let candidates: number[];
	let reasonBase: string;

	if (orig < 5) {
		candidates = candidatesForBand(orig, [99, 49]);
		reasonBase = 'charm ending';
	} else if (orig < 50) {
		candidates =
			type === 'slab' || type === 'sealed'
				? candidatesForBand(orig, [95, 99, 0])
				: candidatesForBand(orig, [99, 95]);
		reasonBase = type === 'slab' ? 'slab premium ending' : 'charm ending';
	} else if (orig < 200) {
		candidates =
			type === 'slab'
				? candidatesForBand(orig, [0, 50, 95])
				: candidatesForBand(orig, [95, 99, 50]);
		reasonBase = type === 'slab' ? 'clean slab ending' : 'premium charm';
	} else if (orig < 1000) {
		candidates = candidatesLargeBand(orig, 5, [0, 5]);
		reasonBase = 'nearest $5 charm';
	} else {
		candidates = candidatesLargeBand(orig, 10, [0]);
		reasonBase = 'nearest $10 step';
	}

	// Filter by drift + unlucky
	const eligible = candidates.filter((c) => withinDrift(orig, c) && !isUnlucky(c));

	if (eligible.length === 0) {
		return {
			suggested: orig,
			reason: 'no candidate within ±2%',
			changed: false,
			deltaPct: 0,
		};
	}

	// Prefer a candidate different from orig; fall back to orig itself
	const best = eligible.find((c) => c !== orig) ?? orig;
	const changed = best !== orig;
	const deltaPct = orig > 0 ? (best - orig) / orig : 0;

	return {
		suggested: best,
		reason: changed ? reasonBase : 'already clean',
		changed,
		deltaPct,
	};
}
